import { useEffect, useMemo, useState } from "react";
import { DatePicker, Table } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import moment from "moment";
import { CalendarOutlined, ShoppingOutlined } from "@ant-design/icons";
import { CREATE_jwel } from "../../Config/Config";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";

// Same shared Inventory components used by BankStatement.jsx / CashBook.jsx /
// OutstandingCustomers.jsx — adjust these three paths if your project's
// shared components live somewhere else.
import styles from "../Pages/shared/Inventory.module.css";
import InventoryPageHeader from "../Pages/shared/InventoryPageHeader";
import InventoryTable from "../Pages/shared/InventoryTable";

const { RangePicker } = DatePicker;

/* ─── filter box theme palette (same as BankStatement / CashBook) ─── */
const FILTER_THEMES = {
  blue: {
    text: "#1d4ed8",
    bg: "#ffffff",
    border: "#93c5fd",
    ring: "rgba(29,78,216,0.15)",
  },
};

const FilterField = ({ icon, label, theme = "blue", width, children }) => {
  const c = FILTER_THEMES[theme];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: c.bg,
        border: `2px solid ${c.border}`,
        borderRadius: 12,
        padding: "7px 10px 9px",
        minHeight: 64,
        width,
        boxShadow: `0 1px 3px ${c.ring}`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: c.text,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {icon} {label}
      </span>
      <div
        style={{
          background: "#fff",
          border: "1.5px solid #d6dbe3",
          borderRadius: 8,
          padding: "1px 8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "CustName", label: "Customer Name" },
  { key: "billno", label: "Inv No" },
];
const secondaryFields = [
  { key: "TOTBASICAMT", label: "Basic Amount" },
  { key: "TOTGST", label: "GST" },
  { key: "totamt", label: "Total Amount" },
];

const SaleRegister = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [openingCash, setOpeningCash] = useState(0);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [loading, setLoading] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const tenantName = localStorage.getItem("tenantName");

  /* ---- data / API logic (unchanged endpoint, query params/formats) ---- */

  useEffect(() => {
    if (!dates[0] || !dates[1]) return;

    setLoading(true);
    setTableVisible(false);

    const fromDate = moment(dayjs(dates[0]).toDate()).format("YYYY-MM-DD");
    const toDate = moment(dayjs(dates[1]).toDate()).format("YYYY-MM-DD");

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhereandOrder?tableName=CREDIT_BILLMAST&where=BILLDATE>='${fromDate}' AND BILLDATE<='${toDate}'&order=billno`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const detailsData = response.data;
        let balance = openingCash;
        const calculatedData = detailsData.map((item, index) => {
          balance = balance + item.DEBIT - item.CREDIT;
          return {
            ...item,
            BALANCE: balance,
            key: index + 1,
            serialNo: index + 1,
          };
        });
        setFilteredData(calculatedData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching cash book details:", error);
      })
      .finally(() => setLoading(false));
  }, [dates, openingCash]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "Inv Date",
      dataIndex: "billdate",
      key: "billdate",
      align: "center",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Inv No",
      dataIndex: "billno",
      key: "billno",
      align: "center",
      render: (value) => (
        <span className="tagBadge">{value?.toFixed?.(0) ?? value}</span>
      ),
    },
    {
      title: "Customer Name",
      dataIndex: "CustName",
      key: "CustName",
      render: (value) => <span style={{ fontWeight: 600 }}>{value}</span>,
    },
    { title: "Mobile Number", dataIndex: "PhoneNo", key: "PhoneNo" },
    {
      title: "QTY",
      dataIndex: "totqty",
      key: "totqty",
      align: "right",
      render: (_, record) => (
        <strong style={{ color: "#ef4444" }}>
          {record?.totqty ?? record?.TOTQTY ?? 0}
        </strong>
      ),
    },
    {
      title: "Basic Amount",
      dataIndex: "TOTBASICAMT",
      key: "TOTBASICAMT",
      align: "right",
      render: (value) => <strong>{Number(value || 0).toFixed(2)}</strong>,
    },
    {
      title: "GST",
      dataIndex: "TOTGST",
      key: "TOTGST",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: "CGST",
      dataIndex: "TOTCGSTAMT",
      key: "TOTCGSTAMT",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: "SGST",
      dataIndex: "TOTSGSTAMT",
      key: "TOTSGSTAMT",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: "IGST",
      dataIndex: "TOTIGSTAMT",
      key: "TOTIGSTAMT",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: "Total Amount",
      dataIndex: "totamt",
      key: "totamt",
      align: "right",
      render: (value) => (
        <strong style={{ color: "#0f766e" }}>
          {Number(value || 0).toFixed(2)}
        </strong>
      ),
    },
  ];

  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, item) => {
        acc.basic += Number(item.TOTBASICAMT || 0);
        acc.gst += Number(item.TOTGST || 0);
        acc.qty += Number(item.totqty ?? item.TOTQTY ?? 0);
        acc.total += Number(item.totamt || 0);
        return acc;
      },
      { basic: 0, gst: 0, qty: 0, total: 0 },
    );
  }, [filteredData]);

  const formattedData = useMemo(
    () =>
      filteredData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        billno: Number(item?.billno?.toFixed?.(0) ?? item?.billno ?? 0),
        totamt: Number(item.totamt || 0).toFixed(2),
        billdate: item?.billdate
          ? dayjs(item.billdate).format("DD/MMM/YYYY")
          : "",
      })),
    [filteredData],
  );

  const exportData = [
    ...formattedData,
    {
      serialNo: "Total",
      billdate: "",
      billno: "",
      CustName: "",
      PhoneNo: "",
      totqty: "",
      TOTBASICAMT: totals.basic.toFixed(2),
      TOTGST: totals.gst.toFixed(2),
      totamt: totals.total.toFixed(2),
    },
  ];

  return (
    <div className={styles.page}>
      <style>{`
        .ant-table-thead > tr > th {
          white-space: nowrap !important;
        }
      `}</style>

      <InventoryPageHeader
        title="Sale Register"
        subtitle="Point Of Sale · Sale Register"
        icon={<ShoppingOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={filteredData.length}
        visible={filteredData.length > 0}
        stats={[
          { label: "Records", value: filteredData.length, color: "blue" },
          { label: "Qty", value: totals.qty, color: "amber" },
          {
            label: "Basic Amount",
            value: totals.basic.toFixed(2),
            color: "green",
          },
          { label: "GST", value: totals.gst.toFixed(2), color: "purple" },
          {
            label: "Total Amount",
            value: totals.total.toFixed(2),
            color: "teal",
          },
        ]}
      />

      <div
        className={styles.toolbar}
        style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
      >
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
        >
          <FilterField
            icon={<CalendarOutlined />}
            label="Date Range"
            theme="blue"
          >
            <RangePicker
              value={dates}
              onChange={(range) => {
                if (range?.[0] && range?.[1]) setDates(range);
              }}
              format="DD/MM/YYYY"
              size="middle"
              bordered={false}
              allowClear={false}
              style={{ width: 240 }}
              disabledDate={(current) =>
                current && current.isAfter(dayjs(), "day")
              }
            />
          </FilterField>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="SaleRegister"
            totals={{
              totalBasicAmount: totals.basic.toFixed(2),
              totalAmount: totals.total.toFixed(2),
            }}
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="mr-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No sale register entries found for the selected filters"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">
              {totals.basic.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.gst.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">
              {totals.total.toFixed(2)}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default SaleRegister;
